import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {WidgetInstallationStatusPill} from '../../../settings/channels/channelCreator/configure/frontChat/widgetInstallationStatusPill';
import {makeGetFrontChatChannelWidgetPreview} from '../../../store/data/channels/channelsSelectors';

/*
 * Props.
 */

interface FrontChatLivePreviewProps {
  channelId: number;
  isInstalled?: boolean;
  isPubliclyVisible: boolean;
  previewWidth?: number;
  previewHeight?: number;
}

/*
 * Style.
 */

const StyledFrontChatLivePreviewWrapperDiv = styled.div<{$width: number}>`
  position: relative;
  width: ${(p) => p.$width}px;
`;

const StyledWidgetBannerDiv = styled.div`
  display: flex;
  padding: 0 24px 10px 24px;
`;

const StyledVisibilityDiv = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: end;
  align-items: center;
  gap: 4px;
`;

const StyledWidgetPreviewIframe = styled.iframe<{$width: number; $height: number}>`
  width: ${(p) => p.$width}px;
  height: ${(p) => p.$height}px;
  border: none;
`;

const StyledBlockExpandChatWindowDiv = styled.div`
  cursor: not-allowed;
  position: absolute;
  width: 32px;
  height: 32px;
  right: 0;
  margin-top: 30px;
  margin-right: 60px;
`;

/*
 * Component.
 */

export const FrontChatLivePreview: FC<FrontChatLivePreviewProps> = (props) => {
  const {channelId, isInstalled, isPubliclyVisible, previewWidth = 430, previewHeight = 670} = props;

  const theme = useUserTheme();
  const getPreviewUrl = useMemo(() => makeGetFrontChatChannelWidgetPreview(channelId), [channelId]);
  const previewUrl = useAppSelector(getPreviewUrl);

  return (
    <StyledFrontChatLivePreviewWrapperDiv $width={previewWidth}>
      <StyledWidgetBannerDiv>
        {isInstalled !== undefined && (
          <>
            <WidgetInstallationStatusPill isInstalled={isInstalled} />
            <StyledVisibilityDiv>
              <Icon
                name={isPubliclyVisible ? 'eye' : 'eyeCrossedFilledSmall'}
                size={20}
                color={theme.greys.shade70}
              />
              {renderVisibilityText(isPubliclyVisible, isInstalled)}
            </StyledVisibilityDiv>
          </>
        )}
      </StyledWidgetBannerDiv>
      <StyledBlockExpandChatWindowDiv />
      <StyledWidgetPreviewIframe
        $width={previewWidth}
        $height={previewHeight}
        title="Preview"
        src={previewUrl}
      />
    </StyledFrontChatLivePreviewWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderVisibilityText(isPubliclyVisible: boolean, isInstalled: boolean) {
  if (isPubliclyVisible && isInstalled) {
    return <Trans id="front-chat-live-preview-widget-ready-visible">Widget visible to visitors</Trans>;
  }

  if (isPubliclyVisible && !isInstalled) {
    return <Trans id="front-chat-live-preview-widget-not-ready-visible">Visible after installation</Trans>;
  }

  if (!isPubliclyVisible && isInstalled) {
    return (
      <Trans id="front-chat-live-preview-widget-ready-not-visible">Widget not visible to visitors</Trans>
    );
  }

  return (
    <Trans id="front-chat-live-preview-widget-not-ready-not-visible">Not visible after installation</Trans>
  );
}
